import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import '@dpem/design-system/tokens.css';
import '@dpem/design-system/tokens/mode-blueprint.css';
import '@dpem/design-system/tokens/mode-editorial.css';
import '@dpem/design-system/tokens/mode-signal.css';

import { App } from './app/App';
import './main.css';

/**
 * The entry point. It does three things and nothing else.
 *
 * The token layer is imported here, first, so every custom property is defined
 * before any CSS module can reference it — a module that loads before the tokens
 * renders once with every one of them unresolved.
 *
 * The three `tokens/mode-*.css` sheets follow it. They are the same layer, split by
 * design language: the Method Explorer renders one method graph in four named
 * presentation modes, three of which are faithful transcriptions of a design
 * prototype that is NOT carbon-precision (a drafting sheet, an editorial spread, a
 * signal canvas). Each sheet defines only `--c-bp-*` / `--c-ed-*` / `--c-sg-*`
 * custom properties, so the platform's own semantic names are untouched and a mode
 * that is not on screen costs nothing but the declarations. Order matters only in
 * that they come after `tokens.css`; they never redefine a name it owns.
 *
 * `tokens.css` rather than the package's `styles.css`: `styles.css` is the built
 * bundle for a consumer installing this package, and the app builds the design
 * system from source (see the aliases in `vite.config.ts`) so that a stale `dist/`
 * in a sibling package can never be what ships. Component CSS modules arrive with
 * the components that import them.
 *
 * `StrictMode` stays on in production. Its double-invoke exposes effects that are
 * not idempotent, and the two places this app has real effects — the session restore
 * and the refresh timer — are exactly where a missed cleanup would show up as a
 * doubled request against the auth endpoint.
 */

const container = document.getElementById('root');
if (container === null) {
  // Not a caught error: there is no app to report it in, and a silent failure here
  // looks identical to a build that produced no JavaScript.
  throw new Error('Root element #root is missing from index.html');
}

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
